import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

export default function KPICard({ 
  title, 
  value, 
  subtitle, 
  icon: Icon, 
  trend,
  trendValue,
  color = 'blue',
  loading = false 
}) {
  // Color gradients for different card types
  const colorSchemes = {
    blue: {
      gradient: 'from-blue-600 to-blue-700',
      bg: 'bg-blue-500/20',
      text: 'text-blue-400',
      shadow: 'shadow-blue-500/20'
    },
    emerald: {
      gradient: 'from-emerald-600 to-emerald-700',
      bg: 'bg-emerald-500/20',
      text: 'text-emerald-400',
      shadow: 'shadow-emerald-500/20'
    },
    amber: {
      gradient: 'from-amber-600 to-orange-600',
      bg: 'bg-amber-500/20',
      text: 'text-amber-400',
      shadow: 'shadow-amber-500/20'
    },
    purple: {
      gradient: 'from-purple-600 to-purple-700',
      bg: 'bg-purple-500/20',
      text: 'text-purple-400',
      shadow: 'shadow-purple-500/20'
    },
    red: {
      gradient: 'from-red-600 to-red-700',
      bg: 'bg-red-500/20',
      text: 'text-red-400',
      shadow: 'shadow-red-500/20'
    },
    cyan: {
      gradient: 'from-cyan-600 to-cyan-700',
      bg: 'bg-cyan-500/20',
      text: 'text-cyan-400',
      shadow: 'shadow-cyan-500/20'
    }
  };

  const scheme = colorSchemes[color] || colorSchemes.blue;

  // Determine trend icon and color
  const getTrendIcon = () => {
    if (!trend) return null;
    
    if (trend === 'up') {
      return <TrendingUp className="w-4 h-4 text-emerald-400" />;
    } else if (trend === 'down') {
      return <TrendingDown className="w-4 h-4 text-red-400" />;
    } else {
      return <Minus className="w-4 h-4 text-slate-400" />;
    }
  };

  const getTrendColor = () => {
    if (trend === 'up') return 'text-emerald-400';
    if (trend === 'down') return 'text-red-400';
    return 'text-slate-400';
  };

  return (
    <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6 hover:border-slate-700 transition-all hover:scale-105 hover:shadow-xl group">
      <div className="flex items-start justify-between mb-4">
        {/* Icon */}
        <div className={`w-12 h-12 bg-gradient-to-br ${scheme.gradient} rounded-xl flex items-center justify-center shadow-lg ${scheme.shadow} group-hover:scale-110 transition-transform`}>
          {Icon && <Icon className="w-6 h-6 text-white" />}
        </div>

        {/* Trend Indicator */}
        {trend && (
          <div className="flex items-center gap-1">
            {getTrendIcon()}
            {trendValue && (
              <span className={`text-sm font-semibold ${getTrendColor()}`}>
                {trendValue}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="space-y-1">
        <p className="text-slate-400 text-sm font-medium">{title}</p>
        
        {loading ? (
          <div className="h-8 bg-slate-800 rounded animate-pulse w-3/4"></div>
        ) : (
          <p className={`text-3xl font-bold ${scheme.text} group-hover:text-white transition-colors`}>
            {value}
          </p>
        )}
        
        {subtitle && (
          <p className="text-slate-500 text-xs mt-1">{subtitle}</p>
        )}
      </div>
    </div>
  );
}